import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter } from '../../../../components/ui/Dialog';
import Button from '../../../../components/ui/Button';
import BlockerPanel from './BlockerPanel';
import AuditLogViewer from './AuditLogViewer';
import MilestonePanel from './MilestonePanel';
import { searchSkills, createSkill } from '../../../../api/Employee/skills';
import {
  EquipmentPriority,
  NeededEquipmentItem,
  GoalSuggestion,
  suggestGoal,
} from '../../../../api/PreformanceManagement/Goals';
import { AssetCategoryView, getCategories as getAssetCategories } from '../../../../api/General/assets';
import type { Goal, GoalDetailModalProps } from '../types';
import {
  mapFrontendLevelToBackend,
  getEligibleParents,
  getParentEligibilityReason,
  levelLabel,
} from '../utils';
import AlertDialog from '../../../../components/ui/AlertDialog';
import { InfoTooltip } from '../../../../components/ui/Tooltip';
import { Skeleton, SkeletonText } from '../../../../components/ui/Skeleton';
import { Spinner } from '../../../../components/ui/Spinner';
import AiGeneratedBadge from '../../../../components/ui/AiGeneratedBadge';
import { useAiFilledFields } from '../../../../hooks/useAiFilledFields';
import EquipmentCategoryPicker from '../../../../components/assets/EquipmentCategoryPicker';
import ClarityMeter from '../../../../components/clarity/ClarityMeter';
import ClaritySavingHint from '../../../../components/clarity/ClaritySavingHint';
import { useCompanyCapabilities } from '../../../../hooks/useCompanyCapabilities';
import { priorityToneClass, priorityOrder } from '@/components/assets/equipmentRequirementChrome';

interface SkillOption {
  id: number;
  name: string;
}

interface FormData {
  title: string;
  description: string;
  status: Goal['status'];
  progress: number;
  owner: string;
  ownerId?: string;
  department: string;
  departmentId?: number;
  startDate: string;
  dueDate: string;
  level: Goal['level'];
  parentId: string;
  metricType: number | null;
  target: string;
  skillIds: number[];
}

const METRIC_TYPE_OPTIONS = [
  { value: 1, labelKey: 'Percentage' },
  { value: 2, labelKey: 'Count' },
  { value: 3, labelKey: 'Time Duration' },
  { value: 4, labelKey: 'Quality Score' },
  { value: 5, labelKey: 'Revenue / Cost' },
  { value: 6, labelKey: 'Satisfaction Score' },
  { value: 7, labelKey: 'Other' },
];

/**
 * The example shown in the target field, keyed to the unit the user just picked.
 *
 * A single generic example ("e.g. 95% customer satisfaction") restated both the unit AND the goal,
 * which is precisely why the field read as a duplicate of Metric Type. An example in the CHOSEN unit
 * answers "how is this different?" without the user having to open the tooltip.
 */
const TARGET_PLACEHOLDER_KEYS: Record<number, string> = {
  1: 'goals.target.placeholder.percentage',
  2: 'goals.target.placeholder.count',
  3: 'goals.target.placeholder.time',
  4: 'goals.target.placeholder.quality',
  5: 'goals.target.placeholder.revenue',
  6: 'goals.target.placeholder.satisfaction',
  7: 'goals.target.placeholder.other',
};

const targetPlaceholderKey = (metricType: number | null): string =>
  (metricType != null && TARGET_PLACEHOLDER_KEYS[metricType]) || 'goals.target.placeholder';

// The fields the goal autofill can write.
type AiField = 'description' | 'metricType' | 'target' | 'neededEquipment';

// Why the parent list is empty, in the user's words.
const PARENT_EMPTY_HINT_KEYS: Record<string, string> = {
  'pick-department': 'goals.link.pickDepartment',
  'no-team-goals': 'goals.link.noTeamGoals',
  'no-company-goals': 'goals.link.noCompanyGoals',
};

const GoalDetailModal: React.FC<GoalDetailModalProps> = ({
  goal = null,
  isOpen = false,
  onClose = () => { },
  onSaveGoal = () => { },
  onDeleteGoal = () => { },
  allGoals = [],
  mode = 'view',
  departments = [],
  loadingDepartments = false,
  availableOwners = [],
  loadingOwners = false,
  detailLoading = false,
}) => {
  const { t } = useTranslation();

  // Gated on `loaded` so authoring buttons never appear and then vanish for a managed company.
  const { can, loaded: capabilitiesLoaded } = useCompanyCapabilities();
  const canAuthor = capabilitiesLoaded && can('canAuthorGoals');

  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    status: 'on-track',
    progress: 0,
    owner: '',
    ownerId: undefined,
    department: '',
    departmentId: undefined,
    startDate: '',
    dueDate: '',
    level: 'individual',
    parentId: '',
    metricType: null,
    target: '',
    skillIds: [],
  });
  const [isEditing, setIsEditing] = useState<boolean>(mode === 'edit' || mode === 'create');
  const [activeTab, setActiveTab] = useState(0);
  const [availableSkills, setAvailableSkills] = useState<SkillOption[]>([]);
  const [knownSkills, setKnownSkills] = useState<Record<number, SkillOption>>({});
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [skillSearchTerm, setSkillSearchTerm] = useState('');

  // Needed-equipment editor + AI assistance state (mirrors RoleEditModal).
  const [categories, setCategories] = useState<AssetCategoryView[]>([]);
  const [neededEquipment, setNeededEquipment] = useState<NeededEquipmentItem[]>(goal?.neededEquipment ?? []);
  const [aiFilling, setAiFilling] = useState(false);
  const [aiNotice, setAiNotice] = useState('');
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [titleOnlyRun, setTitleOnlyRun] = useState(false);
  const [saving, setSaving] = useState(false);
  // The reason a save was refused, shown where the save was attempted. A toast at the foot of the
  // page rendered BEHIND this dialog, which is how a real refusal went unread.
  const [saveError, setSaveError] = useState<string | null>(null);
  const saveErrorRef = useRef<HTMLDivElement>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [aiRationale, setAiRationale] = useState<string>('');
  const [creatingSkill, setCreatingSkill] = useState(false);
  const [skillCreateError, setSkillCreateError] = useState('');
  // Marks the fields the autofill wrote, so each one says where it came from and stops saying it
  // the moment the user edits it.
  const { isAiFilled, markAiFilled, clearAiFilled, resetAiFilled } = useAiFilledFields<AiField>();

  const categoryNameById = new Map(categories.map((c) => [c.id, c.name]));

  // Parse skill IDs from JSON string
  const parseSkillIds = (raw: string | null | undefined): number[] => {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map(Number).filter(n => !isNaN(n));
    } catch {
      // ignore
    }
    return [];
  };

  /**
   * What the form was last seeded from, so a re-render of the SAME goal can be told apart from a
   * different goal or an open/close.
   *
   * `useGoals.handleGoalClick` opens the modal on the list row and then hands it a second `goal`
   * object from `getGoalById`. Before CO-589 that second object re-ran the reset below in full and
   * put `isEditing` back to false, so an Edit click that landed before the detail fetch was
   * silently undone. Only a different goal id, or an open/close (or a mode change), may reset the
   * dialog's mode; a refetch of the goal already on screen merges instead (see `mergeRefetch`).
   */
  const seededFromRef = useRef<{ goalId: string | null; isOpen: boolean; mode: string } | null>(null);
  // The values the form held straight after seeding. A field that still equals its baseline was
  // not touched by the user (or the autofill), so a refetch may refresh it; one that differs is an
  // in-progress edit and is kept.
  const formBaselineRef = useRef<FormData | null>(null);
  const equipmentBaselineRef = useRef<NeededEquipmentItem[]>([]);

  useEffect(() => {
    const sameContext =
      seededFromRef.current !== null
      && seededFromRef.current.goalId === (goal?.id ?? null)
      && seededFromRef.current.isOpen === isOpen
      && seededFromRef.current.mode === mode;

    let nextForm: FormData | null = null;
    let nextEquipment: NeededEquipmentItem[] | null = null;
    if (goal) {
      nextForm = {
        title: goal.title || '',
        description: goal.description || '',
        status: goal.status || 'on-track',
        progress: goal.progress || 0,
        owner: goal.owner || '',
        ownerId: goal.ownerId,
        department: goal.department || '',
        departmentId: goal.departmentId,
        startDate: goal.startDate || '',
        dueDate: goal.dueDate || '',
        level: goal.level || 'individual',
        parentId: goal.parentId || '',
        metricType: goal.metricType ?? null,
        target: goal.target ?? '',
        skillIds: parseSkillIds(goal.skillIds),
      };
      nextEquipment = goal.neededEquipment ?? [];
      // Seed the name lookup from the goal itself. The skill search returns nothing for a
      // blank term, so without this the linked skills render as raw ids until the user
      // happens to search for them.
      if (goal.skills?.length) {
        setKnownSkills(prev => {
          const next = { ...prev };
          goal.skills!.forEach(s => { next[s.id] = { id: s.id, name: s.name }; });
          return next;
        });
      }
    } else if (mode === 'create' && isOpen) {
      // Id and name come from the same row, so they can never describe different departments.
      const defaultDepartment = departments.length > 0 ? departments[0] : undefined;
      nextForm = {
        title: '',
        description: '',
        status: 'on-track',
        progress: 0,
        owner: '',
        ownerId: undefined,
        department: defaultDepartment?.name || '',
        departmentId: defaultDepartment?.id,
        startDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(new Date().setMonth(new Date().getMonth() + 3)).toISOString().split('T')[0],
        level: 'individual',
        parentId: '',
        metricType: null,
        target: '',
        skillIds: [],
      };
      nextEquipment = [];
    }

    if (sameContext) {
      // Merge rule for a refetch of the goal already shown (or departments arriving while a
      // create form is open): per field, an edited value (differs from its baseline) wins; an
      // untouched value is refreshed from the fresh record. Read-only data (milestones, blockers,
      // audit log, resolved skill names, assignments) is rendered from the `goal` prop directly,
      // so it updates regardless. Mode, tab, AI markers and the save error are all left alone.
      if (nextForm && formBaselineRef.current) {
        const baseline = formBaselineRef.current;
        const fresh = nextForm;
        setFormData(prev => {
          const merged = { ...prev };
          (Object.keys(fresh) as (keyof FormData)[]).forEach((key) => {
            const untouched = JSON.stringify(prev[key] ?? null) === JSON.stringify(baseline[key] ?? null);
            if (untouched) (merged as Record<keyof FormData, unknown>)[key] = fresh[key];
          });
          return merged;
        });
        if (nextEquipment) {
          const freshEquipment = nextEquipment;
          setNeededEquipment(prev =>
            JSON.stringify(prev) === JSON.stringify(equipmentBaselineRef.current) ? freshEquipment : prev,
          );
        }
        formBaselineRef.current = fresh;
        equipmentBaselineRef.current = nextEquipment ?? [];
      }
      return;
    }

    if (nextForm) {
      setFormData(nextForm);
      setNeededEquipment(nextEquipment ?? []);
    }
    formBaselineRef.current = nextForm;
    equipmentBaselineRef.current = nextEquipment ?? [];
    seededFromRef.current = { goalId: goal?.id ?? null, isOpen, mode };

    setShowDisclaimer(false);
    setTitleOnlyRun(false);
    setSaveError(null);
    setAiNotice('');
    setAiRationale('');
    // A reopened modal shows saved values, not the last run's AI markers.
    resetAiFilled();
    setIsEditing(mode === 'edit' || mode === 'create');
    setActiveTab(0);
    // resetAiFilled is a stable useCallback; listing it here would not change when this runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goal, mode, isOpen, departments]);

  const statusOptions = [
    { value: 'on-track', label: t('On Track'), color: 'text-primary', icon: 'Target' },
    { value: 'at-risk', label: t('At Risk'), color: 'text-warning', icon: 'AlertTriangle' },
    { value: 'completed', label: t('Completed'), color: 'text-success', icon: 'CheckCircle' },
    { value: 'overdue', label: t('Overdue'), color: 'text-error', icon: 'XCircle' }
  ];

  const levelOptions = [
    { value: 'individual', label: t('Individual Goal') },
    { value: 'team', label: t('Team Goal') },
    { value: 'company', label: t('Company Goal') }
  ];

  const getMetricTypeLabel = (value: number | null): string => {
    if (value === null) return t('Not set');
    const option = METRIC_TYPE_OPTIONS.find(o => o.value === value);
    return option ? t(option.labelKey) : t('Unknown');
  };

  // Fetch skills when editing/creating and search term changes
  const fetchSkills = useCallback(async (term: string) => {
    try {
      setLoadingSkills(true);
      const result = await searchSkills(term || '');
      let mapped: SkillOption[] = [];
      if (result && result.payload && Array.isArray(result.payload)) {
        mapped = result.payload.map((s: { id: number; name: string }) => ({ id: s.id, name: s.name }));
      } else if (result && Array.isArray(result)) {
        mapped = (result as unknown as { id: number; name: string }[]).map(s => ({ id: s.id, name: s.name }));
      }
      setAvailableSkills(mapped);
      if (mapped.length > 0) {
        setKnownSkills(prev => {
          const next = { ...prev };
          mapped.forEach(s => { next[s.id] = s; });
          return next;
        });
      }
    } catch (error) {
      console.error('Failed to fetch skills:', error);
      setAvailableSkills([]);
    } finally {
      setLoadingSkills(false);
    }
  }, []);

  // Search skills while the modal is open and the user has typed a term. A blank term is not sent
  // (CO-590): `SkillService.SearchSkills` returns an empty list for it anyway, and the non-nullable
  // binding answered 400 to `?searchTerm=` on every open. Linked-skill NAMES do not need this call —
  // they are seeded from `goal.skills` below.
  useEffect(() => {
    if (!isOpen) return;
    if (!skillSearchTerm.trim()) {
      setAvailableSkills([]);
      return;
    }
    fetchSkills(skillSearchTerm);
  }, [isOpen, skillSearchTerm, fetchSkills]);

  /**
   * Seed the name cache from the goal's OWN resolved skills.
   *
   * The chips fell back to `#<id>` because they only ever looked in the search results, and
   * `SearchSkills` returns an empty list for a blank term. In view mode there is no search box at
   * all, so a linked skill could never be named there - every chip read `#123`. The server already
   * resolves these (`GoalService.ResolveSkillNames` -> `GoalView.Skills`); this just stops the client
   * throwing that away.
   */
  useEffect(() => {
    if (!isOpen || !goal?.skills?.length) return;
    setKnownSkills((prev) => {
      const next = { ...prev };
      goal.skills!.forEach((s) => { next[s.id] = { id: s.id, name: s.name }; });
      return next;
    });
  }, [isOpen, goal?.skills]);

  // Refetch asset categories on every open so the equipment picker reflects the current company.
  useEffect(() => {
    if (!isOpen) return;
    getAssetCategories().then((result) => {
      if (result.errors.length === 0) setCategories(result.payload ?? []);
    });
  }, [isOpen]);

  const handleSkillToggle = (skillId: number) => {
    setFormData(prev => {
      const current = prev.skillIds;
      if (current.includes(skillId)) {
        return { ...prev, skillIds: current.filter(id => id !== skillId) };
      } else {
        return { ...prev, skillIds: [...current, skillId] };
      }
    });
  };

  const getFilteredOwners = () => {
    if (!formData.departmentId) {
      return availableOwners;
    }
    return availableOwners.filter(owner => owner.departmentId === formData.departmentId);
  };

  const handleOwnerChange = (ownerId: string) => {
    const selectedOwner = availableOwners.find(o => o.id === ownerId);
    if (selectedOwner) {
      setFormData(prev => ({
        ...prev,
        ownerId: selectedOwner.id,
        owner: selectedOwner.fullName,
      }));
    }
  };

  const handleInputChange = (field: keyof FormData, value: FormData[keyof FormData]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Every mutator clears the AI badge first, so it never claims authorship of an edited list.
  const addEquipment = (categoryId: number) => {
    clearAiFilled('neededEquipment');
    if (!categoryId || neededEquipment.some((e) => e.categoryId === categoryId)) return;
    setNeededEquipment([
      ...neededEquipment,
      { categoryId, categoryName: categoryNameById.get(categoryId), priority: EquipmentPriority.Important, quantity: 1 },
    ]);
  };

  const updateEquipmentPriority = (categoryId: number, priority: EquipmentPriority) => {
    clearAiFilled('neededEquipment');
    setNeededEquipment(neededEquipment.map((e) => e.categoryId === categoryId ? { ...e, priority } : e));
  };

  const updateEquipmentQuantity = (categoryId: number, quantity: number) => {
    clearAiFilled('neededEquipment');
    const q = Number.isFinite(quantity) && quantity >= 1 ? Math.floor(quantity) : 1;
    setNeededEquipment(neededEquipment.map((e) => e.categoryId === categoryId ? { ...e, quantity: q } : e));
  };

  const removeEquipment = (categoryId: number) => {
    clearAiFilled('neededEquipment');
    setNeededEquipment(neededEquipment.filter((e) => e.categoryId !== categoryId));
  };

  /**
   * Add a skill the company does not have yet and link it to this goal in one step.
   *
   * Searching for a skill nobody has created returns nothing, and the list said "No skills found"
   * and stopped there: the user had to leave the goal, create the skill elsewhere, and come back.
   */
  const handleCreateSkill = async () => {
    const name = skillSearchTerm.trim();
    if (!name || creatingSkill) return;
    setCreatingSkill(true);
    setSkillCreateError('');
    try {
      const result = await createSkill({ name });
      if (result.errors.length > 0 || !result.payload) {
        setSkillCreateError(result.errors[0]?.description || t('goals.createSkillFailed'));
        return;
      }
      const created = result.payload;
      setKnownSkills((prev) => ({ ...prev, [created.id]: { id: created.id, name: created.name } }));
      setFormData((prev) => ({ ...prev, skillIds: [...prev.skillIds, created.id] }));
      setSkillSearchTerm('');
    } catch {
      setSkillCreateError(t('goals.createSkillFailed'));
    } finally {
      setCreatingSkill(false);
    }
  };

  const buildSuggestInput = () => ({
    title: formData.title.trim(),
    level: formData.level ? mapFrontendLevelToBackend(formData.level) : undefined,
    ownerId: formData.ownerId || undefined,
    parentGoalId: formData.parentId || undefined,
    departmentId: formData.departmentId,
    dueDate: formData.dueDate || undefined,
  });

  const handleAutoFill = async () => {
    if (!formData.title.trim() || aiFilling) return;
    setAiFilling(true);
    setAiNotice('');
    setAiRationale('');
    try {
      const s: GoalSuggestion = await suggestGoal(buildSuggestInput());
      if (s.aiAvailable === false) {
        setAiNotice(t('goals.ai.unavailable'));
        return;
      }
      const filled: AiField[] = [];
      if (s.description) { handleInputChange('description', s.description); filled.push('description'); }
      if (s.metricType != null) { handleInputChange('metricType', s.metricType); filled.push('metricType'); }
      if (s.target) { handleInputChange('target', s.target); filled.push('target'); }
      if (s.neededEquipment?.length) { setNeededEquipment(s.neededEquipment); filled.push('neededEquipment'); }
      if (filled.length) markAiFilled(filled);
      if (s.rationale) setAiRationale(s.rationale);
      const contextProvided = !!(formData.ownerId || formData.parentId || formData.departmentId);
      setTitleOnlyRun(!contextProvided);
      setShowDisclaimer(true);
      // Score what the user can now see, without making them find and press Analyze.
    } catch {
      setAiNotice(t('goals.ai.failed'));
    } finally {
      setAiFilling(false);
    }
  };




  const handleStartEditing = () => {
    if (!canAuthor) return;
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (saving || !canAuthor) return;
    const goalData: Goal = {
      ...formData,
      id: goal?.id || `${formData.level}-${Date.now()}`,
      milestones: goal?.milestones || [],
      metricType: formData.metricType,
      target: formData.target.trim() || null,
      skillIds: formData.skillIds.length > 0 ? JSON.stringify(formData.skillIds) : null,
      neededEquipment,
    };

    setSaving(true);
    setSaveError(null);
    try {
      await onSaveGoal(goalData, mode === 'create');
      setIsEditing(false);
      onClose();
    } catch (err) {
      // Keep the dialog open and say why, here, next to the form that was refused.
      setSaveError(
        err instanceof Error && err.message
          ? err.message
          : t(mode === 'create' ? 'Failed to create goal' : 'Failed to update goal'),
      );
    } finally {
      setSaving(false);
    }
  };

  // A refusal that renders below the fold is a refusal nobody reads. Bring it into view and give it
  // focus, so it is announced rather than merely present.
  useEffect(() => {
    if (!saveError) return;
    const node = saveErrorRef.current;
    if (!node) return;
    node.scrollIntoView({ block: 'start', behavior: 'smooth' });
    node.focus({ preventScroll: true });
  }, [saveError]);

  const handleDelete = () => {
    if (!canAuthor || !goal?.id) return;
    setConfirmDeleteOpen(true);
  };

  // A rejection is left to propagate so the confirm dialog stays open and shows why.
  const handleConfirmDelete = async () => {
    if (!goal?.id) return;
    await onDeleteGoal(goal.id);
    onClose();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const parentGoalOptions = getEligibleParents(formData.level, formData.departmentId, allGoals);
  const parentEmptyHintKey = PARENT_EMPTY_HINT_KEYS[
    getParentEligibilityReason(formData.level, formData.departmentId, parentGoalOptions)
  ];
  const parentFieldLabel = formData.level === 'individual' ? t('goals.link.toTeam') : t('goals.link.toCompany');
  const parentViewLabel = formData.level === 'individual' ? t('goals.link.linkedTeam') : t('goals.link.linkedCompany');

  const handleClose = () => {
    if (saving) return;
    setIsEditing(false);
    onClose();
  };

  if (!isOpen) return null;

  const tabLabels = [t('Details'), t('Milestones'), t('Blockers'), t('Audit Log')];

  return (
    <>
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        size="xl"
        hideCloseButton
        onEscapeKeyDown={(e) => { if (saving) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (saving) e.preventDefault(); }}
        onInteractOutside={(e) => { if (saving) e.preventDefault(); }}
      >
        {/* Title */}
        <DialogHeader className="flex justify-between items-center py-3 px-4 flex-shrink-0">
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            <div className={`p-2 rounded-lg ${formData.level === 'company' ? 'bg-primary/10 text-primary' :
                formData.level === 'team' ? 'bg-warning/10 text-warning' : 'bg-secondary/10 text-secondary'
              }`}>
              <Icon name="Target" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {mode === 'create' ? t('Create New Goal') : formData.title || t('Goal Details')}
              </h2>
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                {levelLabel(t, formData.level)}
                {/* The row data is already on screen; this only says the saved record is still arriving. */}
                {detailLoading && goal && (
                  <Spinner
                    size="sm"
                    className="h-3 w-3 text-muted-foreground"
                    aria-label={t('goals.loadingDetails')}
                    data-testid="goal-detail-inline-loading"
                  />
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            {!isEditing && goal?.id && canAuthor && (
              <>
                <button
                  onClick={handleStartEditing}
                  className="p-2 hover:bg-muted rounded-lg transition-gentle"
                  title={t('Edit Goal')}
                >
                  <Icon name="Edit" size={20} className="text-muted-foreground" />
                </button>
                <button
                  onClick={handleDelete}
                  className="p-2 hover:bg-destructive/10 rounded-lg transition-gentle"
                  title={t('Delete Goal')}
                >
                  <Icon name="Trash2" size={20} className="text-destructive" />
                </button>
              </>
            )}
            <button
              onClick={handleClose}
              disabled={saving}
              aria-label={t('Close')}
              className="p-2 hover:bg-muted rounded-lg transition-gentle disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="X" size={20} className="text-muted-foreground" />
            </button>
          </div>
        </DialogHeader>

        {/* Tabs - only show in view mode for existing goals */}
        {mode === 'view' && goal?.id && (
          <div className="flex border-b border-border px-4 flex-shrink-0">
            {tabLabels.map((label, i) => (
              <button
                key={label}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === i
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                }`}
                onClick={() => setActiveTab(i)}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <DialogBody className="overflow-y-auto flex-1">
          {/* The save was refused. It sits at the top of the body, inside the dialog, because that is
              where the user's attention already is after clicking save. Full border rather than a
              side stripe, and the text tinted from the destructive hue rather than left gray. */}
          {saveError && (
            <div
              ref={saveErrorRef}
              role="alert"
              aria-live="assertive"
              tabIndex={-1}
              data-testid="goal-save-error"
              className="mb-6 flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 p-4 outline-none focus-visible:ring-2 focus-visible:ring-destructive/30"
            >
              <Icon name="AlertCircle" size={18} className="mt-0.5 flex-shrink-0 text-destructive" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-destructive">{t('goals.save.refused')}</p>
                <p className="mt-1 text-sm text-destructive/90 break-words">{saveError}</p>
              </div>
            </div>
          )}

          {/* Deep link: the record is still on its way, so stand in for it rather than show an empty form. */}
          {detailLoading && !goal ? (
            <div
              className="p-6 space-y-6"
              role="status"
              aria-label={t('goals.loadingDetails')}
              data-testid="goal-detail-skeleton"
            >
              <Skeleton className="h-7 w-2/3" aria-hidden="true" />
              <div className="space-y-2">
                <SkeletonText aria-hidden="true" />
                <SkeletonText className="w-5/6" aria-hidden="true" />
              </div>
              <div className="space-y-2">
                <SkeletonText className="h-3 w-24" aria-hidden="true" />
                <Skeleton className="h-3 w-full rounded-full" aria-hidden="true" />
              </div>
            </div>
          ) : (
          <>
          {/* Milestones Tab */}
          {activeTab === 1 && mode === 'view' && goal?.id && (
            <MilestonePanel
              goalId={goal.id}
              goalStartDate={goal.startDate}
              goalDueDate={goal.dueDate}
              isOpen={activeTab === 1}
              canEdit={canAuthor}
            />
          )}

          {/* Blockers Tab */}
          {activeTab === 2 && mode === 'view' && goal?.id && (
            <BlockerPanel goalId={goal.id} isOpen={activeTab === 2} canEdit={canAuthor} />
          )}

          {/* Audit Log Tab */}
          {activeTab === 3 && mode === 'view' && goal?.id && (
            <AuditLogViewer goalId={goal.id} isOpen={activeTab === 3} />
          )}

          {/* Details Tab (or edit/create mode) */}
          {(activeTab === 0 || mode !== 'view') && (
          <div className="p-6 space-y-6">
            {/* Start with AI — a top-of-form starter (prominent in create, light in edit) */}
            {isEditing && (
              <div className="space-y-3">
                {mode === 'create' ? (
                  <div className="flex flex-col gap-4 rounded-xl border border-primary/20 bg-primary/5 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-3 min-w-0">
                      <span className="grid place-items-center w-9 h-9 rounded-lg bg-primary/10 text-primary flex-shrink-0">
                        <Icon name="Sparkles" size={18} />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground">{t('goals.ai.autoFillTitle')}</p>
                        <p className="text-xs text-muted-foreground">{t('goals.ai.autoFillSubtitle')}</p>
                      </div>
                    </div>
                    <Button
                      onClick={handleAutoFill}
                      disabled={!formData.title.trim() || aiFilling}
                      loading={aiFilling}
                      iconName="Sparkles"
                      size="lg"
                      className="sm:flex-shrink-0"
                      data-testid="goal-ai-autofill-button"
                    >
                      {t('goals.ai.autoFill')}
                    </Button>
                  </div>
                ) : null /* Edit: no AI redraft. */}

                {showDisclaimer && (
                  <div className="flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-primary">
                    <Icon name="Info" size={16} className="mt-0.5 flex-shrink-0" />
                    <span>{t('goals.ai.disclaimer')}</span>
                  </div>
                )}

                {titleOnlyRun && (
                  <div className="flex items-start gap-2 rounded-lg border border-warning/20 bg-warning/5 px-4 py-3 text-sm text-warning">
                    <Icon name="AlertTriangle" size={16} className="mt-0.5 flex-shrink-0" />
                    <span>{t('goals.ai.titleOnlyNudge')}</span>
                  </div>
                )}

                {aiNotice && (
                  <div className="flex items-start justify-between gap-2 rounded-lg border border-warning/20 bg-warning/5 px-4 py-3 text-sm text-warning">
                    <span>{aiNotice}</span>
                    <button type="button" onClick={() => setAiNotice('')} className="text-warning hover:opacity-70" aria-label={t('goals.remove')}>
                      <Icon name="X" size={16} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                {isEditing ? (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('Goal Title')}
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder={t('Enter goal title...')}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                      required
                    />
                  </div>
                ) : (
                  <div>
                    <h3 className="font-medium text-foreground mb-2">{t('Goal Title')}</h3>
                    <p className="text-foreground">{formData.title}</p>
                  </div>
                )}
              </div>

              <div>
                {isEditing ? (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('Goal Level')}
                    </label>
                    <select
                      value={formData.level}
                      onChange={(e) => {
                        handleInputChange('level', e.target.value);
                        handleInputChange('parentId', '');
                      }}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    >
                      {levelOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div>
                    <h3 className="font-medium text-foreground mb-2">{t('Level')}</h3>
                    <p className="text-foreground">{levelLabel(t, formData.level)}</p>
                  </div>
                )}
              </div>

              <div>
                {isEditing ? (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('Department')} {loadingDepartments && t('(Loading...)')}
                    </label>
                   <select
  value={formData.departmentId || ''}
  onChange={(e) => {
    const selectedDeptId = e.target.value ? parseInt(e.target.value) : undefined;
    const selectedDept = departments.find(d => d.id === selectedDeptId);
    handleInputChange('departmentId', selectedDeptId);
    handleInputChange('department', selectedDept?.name || '');
    // The chosen parent belongs to the old department, so it may no longer be eligible.
    handleInputChange('parentId', '');
  }}
  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
  disabled={loadingDepartments || departments.length === 0}
>
  <option value="">{t('Select Department (Optional)')}</option>
  {departments.map(dept => (
    <option key={dept.id} value={dept.id}>{dept.name}</option>
  ))}
  {!loadingDepartments && departments.length === 0 && (
    <option disabled value="">{t('No departments available')}</option>
  )}
</select>
                  </div>
                ) : (
                  <div>
                    <h3 className="font-medium text-foreground mb-2">{t('Department')}</h3>
                    <p className="text-foreground">{formData.department || t('Not assigned')}</p>
                  </div>
                )}
              </div>

              <div>
                {isEditing && mode === 'create' ? (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('Owner')} {loadingOwners && t('(Loading...)')}
                    </label>
                    <select
                      value={formData.ownerId || ''}
                      onChange={(e) => handleOwnerChange(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                      disabled={loadingOwners || availableOwners.length === 0}
                    >
                      <option value="">{t('Select Owner')}</option>
                      {getFilteredOwners().map(owner => (
                        <option key={owner.id} value={owner.id}>
                          {owner.fullName} {owner.departmentName ? `(${owner.departmentName})` : ''}
                        </option>
                      ))}
                      {!loadingOwners && getFilteredOwners().length === 0 && (
                        <option disabled value="">{t('No owners available for selected department')}</option>
                      )}
                    </select>
                    {getFilteredOwners().length > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('{{count}} owner(s) available', { count: getFilteredOwners().length })}
                        {formData.departmentId && ` ${t('for selected department')}`}
                      </p>
                    )}
                  </div>
                ) : isEditing && mode === 'edit' ? (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('Owner')}
                    </label>
                    <input
                      type="text"
                      value={formData.owner}
                      disabled
                      className="w-full px-3 py-2 border border-border rounded-lg bg-muted text-muted-foreground cursor-not-allowed"
                    />
                  </div>
                ) : (
                  <div>
                    <h3 className="font-medium text-foreground mb-2">{t('Owner')}</h3>
                    <p className="text-foreground">{formData.owner}</p>
                  </div>
                )}
              </div>

              <div>
                {isEditing ? (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('Progress')} ({formData.progress}%)
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={formData.progress}
                      onChange={(e) => handleInputChange('progress', parseInt(e.target.value))}
                      className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                ) : (
                  <div>
                    <h3 className="font-medium text-foreground mb-3">{t('Progress')}</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">{t('Completion')}</span>
                        <span className="text-lg font-semibold text-foreground">
                          {formData.progress}%
                        </span>
                      </div>
                      <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-500"
                          style={{ width: `${formData.progress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div>
              {isEditing ? (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label className="block text-sm font-medium text-foreground">
                      {t('Description')}
                    </label>
                    <AiGeneratedBadge visible={isAiFilled('description')} />
                  </div>
                  <textarea
                    value={formData.description}
                    onChange={(e) => { clearAiFilled('description'); handleInputChange('description', e.target.value); }}
                    placeholder={t('Enter goal description...')}
                    rows={4}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none"
                  />
                </div>
              ) : (
                <div>
                  <h3 className="font-medium text-foreground mb-2">{t('Description')}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {formData.description}
                  </p>
                </div>
              )}
            </div>

            {/* Measurement (metric type + its target value) & Skills */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <label className="block text-sm font-medium text-foreground">
                          {t('Metric Type')}
                        </label>
                        <AiGeneratedBadge visible={isAiFilled('metricType')} />
                      </div>
                      <select
                        value={formData.metricType ?? ''}
                        onChange={(e) => {
                          const val = e.target.value ? parseInt(e.target.value) : null;
                          clearAiFilled('metricType');
                          setFormData(prev => ({ ...prev, metricType: val }));
                        }}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                      >
                        <option value="">{t('Select Metric Type (Optional)')}</option>
                        {METRIC_TYPE_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                        ))}
                      </select>
                      {formData.metricType === 7 && (
                        <div className="mt-2 flex items-center gap-2 text-sm text-warning bg-warning/10 p-2 rounded-lg">
                          <Icon name="AlertTriangle" size={16} className="text-warning flex-shrink-0" />
                          <span>{t('Consider choosing a specific metric type for better tracking and reporting.')}</span>
                        </div>
                      )}
                    </div>

                    {/* Metric type is the UNIT; this is the VALUE that ends the goal. Naming the field
                        "Target value" and keying its example to the chosen unit is what stops the two
                        reading as the same question. */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <label className="block text-sm font-medium text-foreground" htmlFor="goal-target-input">
                          {t('Target value')}
                        </label>
                        <InfoTooltip content={t('goals.target.tooltip')} label={t('Target value')} data-testid="goal-target-tooltip" />
                        <AiGeneratedBadge visible={isAiFilled('target')} />
                      </div>
                      <input
                        id="goal-target-input"
                        type="text"
                        value={formData.target}
                        onChange={(e) => { clearAiFilled('target'); handleInputChange('target', e.target.value); }}
                        placeholder={t(targetPlaceholderKey(formData.metricType))}
                        maxLength={500}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                        data-testid="goal-target-input"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-medium text-foreground mb-2">{t('Metric Type')}</h3>
                      <p className={formData.metricType === null ? 'text-muted-foreground' : 'text-foreground'}>
                        {getMetricTypeLabel(formData.metricType)}
                      </p>
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground mb-2">{t('Target value')}</h3>
                      <p className={formData.target ? 'text-foreground break-words' : 'text-muted-foreground'}>
                        {formData.target || t('Not set')}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div>
                {isEditing ? (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('Linked Skills')} {loadingSkills && t('(Loading...)')}
                    </label>
                    <input
                      type="text"
                      value={skillSearchTerm}
                      onChange={(e) => setSkillSearchTerm(e.target.value)}
                      placeholder={t('Search skills...')}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary mb-2"
                    />
                    {formData.skillIds.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {formData.skillIds.map(skillId => {
                          const skill = availableSkills.find(s => s.id === skillId) || knownSkills[skillId];
                          return (
                            <span
                              key={skillId}
                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full"
                            >
                              {skill?.name || `#${skillId}`}
                              <button
                                type="button"
                                onClick={() => handleSkillToggle(skillId)}
                                className="hover:text-destructive transition-colors"
                              >
                                <Icon name="X" size={12} />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <div className="max-h-32 overflow-y-auto border border-border rounded-lg">
                      {availableSkills.length === 0 && !loadingSkills && !skillSearchTerm.trim() && (
                        <p className="p-2 text-sm text-muted-foreground text-center">{t('No skills found')}</p>
                      )}
                      {skillSearchTerm.trim().length > 0
                        && !availableSkills.some((s) => s.name.toLowerCase() === skillSearchTerm.trim().toLowerCase())
                        && !loadingSkills && (
                        <button
                          type="button"
                          onClick={handleCreateSkill}
                          disabled={creatingSkill}
                          className="flex w-full items-center gap-2 px-3 py-2 text-start text-sm font-medium text-primary hover:bg-muted disabled:opacity-60"
                          data-testid="goal-skill-create"
                        >
                          <Icon
                            name={creatingSkill ? 'Loader2' : 'Plus'}
                            size={14}
                            className={creatingSkill ? 'animate-spin' : ''}
                          />
                          {t('goals.createSkillOption', { name: skillSearchTerm.trim() })}
                        </button>
                      )}
                      {availableSkills.map(skill => (
                        <label
                          key={skill.id}
                          className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/50 cursor-pointer text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={formData.skillIds.includes(skill.id)}
                            onChange={() => handleSkillToggle(skill.id)}
                            className="rounded border-border text-primary focus:ring-primary/20"
                          />
                          <span className="text-foreground">{skill.name}</span>
                        </label>
                      ))}
                    </div>
                    {skillCreateError && (
                      <p className="mt-1 text-xs text-destructive" data-testid="goal-skill-create-error">
                        {skillCreateError}
                      </p>
                    )}
                  </div>
                ) : (
                  <div>
                    <h3 className="font-medium text-foreground mb-2">{t('Linked Skills')}</h3>
                    {formData.skillIds.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {formData.skillIds.map(skillId => {
                          const skill = availableSkills.find(s => s.id === skillId) || knownSkills[skillId];
                          return (
                            <span
                              key={skillId}
                              className="inline-flex items-center px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full"
                            >
                              {skill?.name || `#${skillId}`}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-muted-foreground">{t('None')}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Needed Equipment (edit/create only) */}
            {isEditing && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm font-medium text-foreground">{t('goals.neededEquipment')}</label>
                  <AiGeneratedBadge visible={isAiFilled('neededEquipment')} />
                </div>
                {aiRationale && (
                  <div className="flex items-start gap-2 mb-3 rounded-lg border border-primary/15 bg-primary/5 px-3 py-2 text-xs text-muted-foreground">
                    <Icon name="Sparkles" size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                    <span><span className="font-medium text-foreground">{t('goals.ai.rationaleLabel')}</span> {aiRationale}</span>
                  </div>
                )}
                <div className="space-y-2 mb-2">
                  {neededEquipment.map((item) => (
                    <div key={item.categoryId} className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2">
                      <span className="flex-1 text-sm text-foreground">
                        {item.categoryName ?? categoryNameById.get(item.categoryId) ?? String(item.categoryId)}
                      </span>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-muted-foreground">{t('goals.quantity')}</span>
                        <input
                          type="number"
                          min={1}
                          value={item.quantity ?? 1}
                          onChange={(e) => updateEquipmentQuantity(item.categoryId, parseInt(e.target.value, 10))}
                          className="w-16 rounded-lg border border-border bg-card px-2 py-1 text-xs font-medium focus-visible:ring-2 focus-visible:ring-primary/20"
                          data-testid={`goal-equipment-quantity-${item.categoryId}`}
                        />
                        <span className="text-xs text-muted-foreground ms-1">{t('goals.priority.label')}</span>
                        <select
                          value={item.priority}
                          onChange={(e) => updateEquipmentPriority(item.categoryId, e.target.value as EquipmentPriority)}
                          className={`rounded-lg border px-2 py-1 text-xs font-medium focus-visible:ring-2 focus-visible:ring-primary/20 ${priorityToneClass[item.priority]}`}
                          data-testid={`goal-equipment-priority-${item.categoryId}`}
                        >
                          {priorityOrder.map((p) => (
                            <option key={p} value={p}>{t(`goals.priority.${p.toLowerCase()}`)}</option>
                          ))}
                        </select>
                      </div>
                      <button type="button" onClick={() => removeEquipment(item.categoryId)} className="text-muted-foreground hover:text-destructive" aria-label={t('goals.remove')}>
                        <Icon name="X" size={16} />
                      </button>
                    </div>
                  ))}
                </div>
                {/* Same picker as the role form: an equipment category missing from the company can
                    be added here, in the group the user chooses, without leaving the goal. */}
                <EquipmentCategoryPicker
                  categories={categories}
                  excludeIds={neededEquipment.map((e) => e.categoryId)}
                  onPick={(categoryId) => addEquipment(categoryId)}
                  onCreated={(created) => setCategories((prev) => [...prev, created])}
                  data-testid="goal-equipment-add-select"
                />
              </div>
            )}

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                {isEditing ? (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('Start Date')}
                    </label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => handleInputChange('startDate', e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    />
                  </div>
                ) : (
                  <div>
                    <h3 className="font-medium text-foreground mb-2">{t('Start Date')}</h3>
                    <p className="text-foreground">{formatDate(formData.startDate)}</p>
                  </div>
                )}
              </div>

              <div>
                {isEditing ? (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('Due Date')}
                    </label>
                    <input
                      type="date"
                      value={formData.dueDate}
                      onChange={(e) => handleInputChange('dueDate', e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    />
                  </div>
                ) : (
                  <div>
                    <h3 className="font-medium text-foreground mb-2">{t('Due Date')}</h3>
                    <p className="text-foreground">{formatDate(formData.dueDate)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Parent Goal Linking. A company goal has no parent, so the field does not exist there. */}
            {formData.level !== 'company' && (
              <div>
                {isEditing ? (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label className="block text-sm font-medium text-foreground" htmlFor="goal-parent-select">
                        {parentFieldLabel}
                      </label>
                      <InfoTooltip content={t('goals.link.tooltip')} label={parentFieldLabel} data-testid="goal-parent-tooltip" />
                    </div>
                    <select
                      id="goal-parent-select"
                      value={formData.parentId}
                      onChange={(e) => handleInputChange('parentId', e.target.value)}
                      disabled={parentGoalOptions.length === 0}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed"
                      data-testid="goal-parent-select"
                    >
                      <option value="">{t('goals.link.none')}</option>
                      {parentGoalOptions.map(g => (
                        <option key={g.id} value={g.id}>{g.title}</option>
                      ))}
                    </select>
                    {parentGoalOptions.length === 0 && parentEmptyHintKey && (
                      <p className="text-xs text-muted-foreground mt-1" data-testid="goal-parent-empty-hint">
                        {t(parentEmptyHintKey)}
                      </p>
                    )}
                  </div>
                ) : (
                  <div>
                    <h3 className="font-medium text-foreground mb-2">{parentViewLabel}</h3>
                    <p className="text-foreground" data-testid="goal-parent-value">
                      {allGoals.find(g => g.id === formData.parentId)?.title || t('goals.link.none')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Status */}
            <div>
              <h3 className="font-medium text-foreground mb-3">
                {isEditing ? t('Update Status') : t('Current Status')}
              </h3>
              {isEditing ? (
                <div className="grid grid-cols-2 gap-3">
                  {statusOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleInputChange('status', option.value)}
                      className={`flex items-center space-x-2 rtl:space-x-reverse p-3 rounded-lg border-2 transition-gentle ${formData.status === option.value
                          ? 'border-primary bg-primary/10' : 'border-border hover:border-muted-foreground'
                        }`}
                    >
                      <Icon name={option.icon} size={16} className={option.color} />
                      <span className="text-sm font-medium text-foreground">
                        {option.label}
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  {statusOptions.map((option) =>
                    option.value === formData.status && (
                      <div key={option.value} className="flex items-center space-x-2 rtl:space-x-reverse">
                        <Icon name={option.icon} size={16} className={option.color} />
                        <span className="font-medium text-foreground">{option.label}</span>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>

            {/* Milestone Summary */}
            {goal?.milestones && goal.milestones.length > 0 && !isEditing && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-foreground">{t('Key Milestones')}</h3>
                  <button
                    onClick={() => setActiveTab(1)}
                    className="text-xs text-primary hover:text-primary/80 transition-colors"
                    data-testid="view-all-milestones-btn"
                  >
                    {t('View All')} →
                  </button>
                </div>
                <div className="space-y-2">
                  {goal.milestones.slice(0, 3).map((milestone, index) => (
                    <div key={index} className="flex items-center space-x-3 rtl:space-x-reverse p-2 bg-muted/50 rounded-lg">
                      <Icon
                        name={milestone.completed ? "CheckCircle" : "Circle"}
                        size={16}
                        className={milestone.completed ? "text-success" : "text-muted-foreground"}
                      />
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${milestone.completed ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {milestone.title}
                        </p>
                      </div>
                    </div>
                  ))}
                  {goal.milestones.length > 3 && (
                    <p className="text-xs text-muted-foreground text-center">
                      {t('+{{count}} more', { count: goal.milestones.length - 3 })}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Clarity. Read-only, and only on a saved goal: the score is recalculated server-side
                on every save, so an "analyze" control inside the form produced a preview the save
                could then disagree with. Editing the goal is what changes the number. */}
            {!isEditing && goal?.id && (
              <ClarityMeter
                clarity={goal.clarity}
                score={goal.clarityScore}
                updatedOn={goal.clarityScoreUpdatedOn}
                headingKey="goals.ai.clarity"
                hintKey="goals.ai.claritySubtitle"
                emptyKey="goals.ai.clarityEmpty"
                howScoredKey="goals.ai.clarityHowScored"
                feedsKey="goals.ai.clarityFeedsQ1"
                fieldLabelKey="goals.ai.clarityField"
                data-testid="goal-clarity-panel"
                testIdPrefix="goal-clarity"
              />
            )}
          </div>
          )}
          </>
          )}
        </DialogBody>

        {/* Actions */}
        <DialogFooter className="px-4 py-3 flex-shrink-0">
          <ClaritySavingHint visible={saving} className="me-auto" />
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            {isEditing ? (
              <>
                <Button
                  variant="outline"
                  disabled={saving}
                  onClick={() => {
                    if (saving) return;
                    setIsEditing(false);
                    if (mode === 'create') onClose();
                  }}
                >
                  {t('Cancel')}
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!formData.title.trim() || saving}
                  loading={saving}
                  data-testid="goal-save-button"
                >
                  {mode === 'create' ? t('Create Goal') : t('Save Changes')}
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={handleClose}>
                {t('Close')}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <AlertDialog
      open={confirmDeleteOpen}
      onOpenChange={setConfirmDeleteOpen}
      title={t('goals.delete.title')}
      description={t('goals.delete.description')}
      confirmLabel={t('goals.delete.confirm')}
      variant="destructive"
      onConfirm={handleConfirmDelete}
    />
    </>
  );
};

export default GoalDetailModal;
